import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { AdminHeader } from "@/components/admin/parts";
import { ApprovalQueue } from "@/components/admin/wi/approve/ApprovalQueue";
import { ApprovalHistory } from "@/components/admin/wi/approve/ApprovalHistory";
import { ApproveDrawer } from "@/components/admin/wi/approve/ApproveDrawer";
import { OutlookCard } from "@/components/admin/wi/approve/OutlookCard";
import { SyncBadge } from "@/components/admin/wi/approve/SyncBadge";
import { btnGhost } from "@/components/admin/wi/ui";
import { adminApi, getErrorMessage } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";

export default function WiApprovals() {
  const [queue, setQueue] = useState(null);
  const [hist, setHist] = useState(null);
  const [page, setPage] = useState(0);
  const [outlook, setOutlook] = useState(null);
  const [open, setOpen] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [newIds, setNewIds] = useState(() => new Set());
  const known = useRef(null);
  const [params, setParams] = useSearchParams();
  const gotQueue = useCallback((data) => {
    const ids = data.rows.map((r) => r.id);
    const fresh = known.current ? ids.filter((i) => !known.current.has(i)) : [];
    if (fresh.length) {
      toast.info(`${fresh.length} new claim${fresh.length > 1 ? "s" : ""} waiting for a decision`);
      setNewIds((prev) => new Set([...prev, ...fresh]));
    }
    known.current = new Set(ids);
    setQueue(data);
    setLoadErr(null);
  }, []);
  const load = useCallback(() => {
    const fail = (e) => setLoadErr(getErrorMessage(e));
    adminApi.get("/admin/wi/approvals/queue").then(({ data }) => gotQueue(data)).catch(fail);
    adminApi.get("/admin/wi/approvals", { params: { page } }).then(({ data }) => setHist(data)).catch(fail);
    adminApi.get("/admin/wi/outlook").then(({ data }) => setOutlook(data)).catch(fail);
  }, [page, gotQueue]);
  // every minute: ask the server to check Monday (shared and throttled server-side), then reload the lists
  const tick = useCallback(async () => {
    try {
      const { data } = await adminApi.post("/admin/wi/approvals/refresh", null, { params: { auto: true } });
      if (data.started) await new Promise((r) => setTimeout(r, 6000));
    } catch { /* the lists still reload below */ }
    load();
  }, [load]);
  useEffect(() => { tick(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (known.current) load(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps
  usePolling(tick, 60000);
  useEffect(() => {
    const id = params.get("claim");
    if (id) { setOpen({ claim_id: id }); setParams({}, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = async () => {
    try { await adminApi.post("/admin/wi/approvals/refresh"); toast.success("Checking Monday.com for new claims…"); setTimeout(load, 4000); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <div data-testid="wi-approvals-page">
      <AdminHeader title="Approvals" subtitle="A person approves each claim. The app then gives it the next OTMA number, updates both Monday.com boards and gets the emails ready."
        actions={<div className="flex flex-col items-end gap-2"><button className={btnGhost} onClick={refresh} data-testid="wi-approvals-refresh-btn"><ArrowsClockwise size={15} /> Check Monday for new claims</button>
          <SyncBadge sync={queue?.sync} /></div>} />
      {loadErr && <div className="mx-8 mt-6 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3" data-testid="wi-approvals-load-error">Couldn't load approvals: {loadErr}</div>}
      <div className="p-8 grid xl:grid-cols-3 gap-6 items-start">
        <div className="xl:col-span-2 space-y-6">
          <ApprovalQueue data={queue} newIds={newIds} onOpen={(r) => setOpen({ ...r, claim_id: r.id })} />
          <ApprovalHistory data={hist} page={page} setPage={setPage} onOpen={(a) => setOpen({ ...a, claim_id: a.claim_id, approval: a })} />
        </div>
        <OutlookCard o={outlook} onChange={setOutlook} />
      </div>
      {open && <ApproveDrawer key={open.claim_id} target={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
